import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const STAGES = [
    { key: "creating", label: "Creating checkout session", ms: 550 },
    { key: "reserving", label: "Reserving your order with our payment partner", ms: 850 },
    { key: "provisioning", label: "Provisioning workspace credits", ms: 700 },
    { key: "activating", label: "Activating your new plan", ms: 500 },
];

export default function BillingCheckout() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();

    const [session, setSession] = useState(null);
    const [error, setError] = useState(null);
    const [stageIdx, setStageIdx] = useState(0);
    const [phase, setPhase] = useState("processing"); // processing | success | failed
    const [subscription, setSubscription] = useState(null);
    const startedRef = useRef(false);

    useEffect(() => {
        api.get(`/billing/checkout/${sessionId}`)
            .then(({ data }) => {
                setSession(data);
                if (data.status === "completed") {
                    navigate(`/checkout/success/${sessionId}`, { replace: true });
                    return;
                }
                if (data.status === "cancelled") setPhase("failed");
            })
            .catch(() => setError("This checkout session no longer exists."));
    }, [sessionId]);

    // Drive the fake processing timeline once the session loads
    useEffect(() => {
        if (!session || phase !== "processing" || startedRef.current) return;
        startedRef.current = true;

        const controller = new AbortController();
        const external = /^https?:/.test(session.hosted_url || "");
        (async () => {
            if (external) {
                // Live provider: activation is webhook-confirmed, poll status.
                setStageIdx(STAGES.length - 1);
                for (let n = 0; n < 60; n += 1) {
                    if (controller.signal.aborted) return;
                    try {
                        const { data } = await api.get(`/billing/checkout/${sessionId}`);
                        if (data.status === "completed") {
                            navigate(`/checkout/success/${sessionId}`, { replace: true });
                            return;
                        }
                        if (data.status === "cancelled") {
                            setPhase("failed");
                            setError("The checkout was cancelled before payment completed.");
                            return;
                        }
                    } catch { /* keep polling */ }
                    await new Promise((r) => setTimeout(r, 3000));
                }
                setPhase("failed");
                setError("We haven't received payment confirmation yet. If you completed payment, your plan will activate automatically within a few minutes.");
                return;
            }
            for (let i = 0; i < STAGES.length; i += 1) {
                if (controller.signal.aborted) return;
                setStageIdx(i);
                await new Promise((r) => setTimeout(r, STAGES[i].ms));
            }
            try {
                const { data } = await api.post(
                    `/billing/checkout/${sessionId}/complete`,
                    {},
                    { signal: controller.signal },
                );
                if (controller.signal.aborted) return;
                setSession(data.session);
                setSubscription(data.subscription);
                await refresh();
                navigate(`/checkout/success/${sessionId}`, { replace: true });
            } catch (err) {
                if (controller.signal.aborted) return;
                const detail = err?.response?.data?.detail;
                setPhase("failed");
                setError(typeof detail === "string" ? detail : "Checkout failed. Please retry.");
                toast.error("Checkout failed");
            }
        })();
        // Cleanup: reset guard so React 18 StrictMode's dev-mode remount
        // (or a real navigation-back) can re-run the timeline once instead of
        // permanently stalling at stage 1.
        return () => {
            controller.abort();
            startedRef.current = false;
        };
    }, [session, phase, sessionId, refresh]);

    async function cancel() {
        try {
            await api.post(`/billing/checkout/${sessionId}/cancel`, {});
            toast("Checkout cancelled");
            navigate("/app/pricing");
        } catch {
            navigate("/app/pricing");
        }
    }

    if (error && !session) {
        return (
            <FailureShell
                message={error}
                onRetry={() => navigate("/app/pricing")}
                retryLabel="Back to pricing"
            />
        );
    }

    if (!session) {
        return (
            <div className="container-edit py-20 flex justify-center">
                <Loader2 className="animate-spin" color="#0D1B3E" />
            </div>
        );
    }

    if (phase === "failed") {
        return (
            <FailureShell
                message={error || "We couldn't finalise this order."}
                onRetry={() => navigate("/app/pricing")}
                retryLabel="Back to pricing"
            />
        );
    }

    if (phase === "success") {
        return (
            <SuccessShell
                session={session}
                subscription={subscription}
                onContinue={() => navigate("/app")}
            />
        );
    }

    return (
        <ProcessingShell
            session={session}
            stageIdx={stageIdx}
            onCancel={cancel}
        />
    );
}

// --- Sub-components ---------------------------------------------------------

function ProcessingShell({ session, stageIdx, onCancel }) {
    const external = /^https?:/.test(session.hosted_url || "");
    return (
        <div className="container-edit py-12 lg:py-20 max-w-2xl" data-testid="billing-checkout-processing">
            <div className="label-eyebrow">Secure checkout</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                {external ? "Activating your plan…" : "Processing your order…"}
            </h1>
            <p className="mt-3 text-[#0D1B3E]/70">
                {external
                    ? "We're confirming your payment. This usually takes just a few seconds."
                    : "Confirming your order. This will only take a moment."}
            </p>

            <div className="mt-10 card-flat p-8">
                <OrderSummary session={session} />
                <hr className="gold-rule my-7" />
                <ol className="space-y-3">
                    {STAGES.map((s, i) => {
                        const done = i < stageIdx;
                        const active = i === stageIdx;
                        return (
                            <li
                                key={s.key}
                                className="flex items-center gap-3 text-sm"
                                data-testid={`billing-stage-${s.key}`}
                            >
                                {done ? (
                                    <CheckCircle2 size={18} color="#C9A84C" />
                                ) : active ? (
                                    <Loader2 size={18} className="animate-spin" color="#0D1B3E" />
                                ) : (
                                    <span className="inline-block w-[18px] h-[18px] rounded-full border border-[#0D1B3E]/25" />
                                )}
                                <span
                                    className={
                                        done
                                            ? "text-[#0D1B3E]/60 line-through"
                                            : active
                                              ? "text-[#0D1B3E] font-medium"
                                              : "text-[#0D1B3E]/45"
                                    }
                                >
                                    {s.label}
                                </span>
                            </li>
                        );
                    })}
                </ol>

                <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                    <ShieldCheck size={14} color="#C9A84C" />
                    Secure checkout. Your card details never touch our servers
                </div>
            </div>

            <button
                type="button"
                onClick={onCancel}
                className="mt-8 text-sm underline text-[#0D1B3E]/60 hover:text-[#0D1B3E]"
                data-testid="billing-checkout-cancel"
            >
                Cancel and go back
            </button>
        </div>
    );
}

function SuccessShell({ session, subscription, onContinue }) {
    return (
        <div className="container-edit py-12 lg:py-20 max-w-2xl" data-testid="billing-checkout-success">
            <div className="flex items-center gap-3 text-[#C9A84C]">
                <CheckCircle2 size={22} />
                <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    Payment successful
                </span>
            </div>
            <h1 className="font-display text-3xl lg:text-4xl mt-3 tracking-tight">
                Welcome to {subscription?.plan_name ?? session.plan_key}.
            </h1>
            <p className="mt-3 text-[#0D1B3E]/75 max-w-lg">
                Your credits have been refreshed and the full feature set is now unlocked
                on your workspace. Your receipt and VAT invoice will arrive by email.
            </p>

            <div className="mt-10 card-flat p-8">
                <OrderSummary session={session} />
                {subscription && (
                    <>
                        <hr className="gold-rule my-7" />
                        <div className="grid grid-cols-2 gap-y-3 text-sm">
                            <span className="text-[#0D1B3E]/60">Subscription</span>
                            <span className="text-right font-mono text-xs" data-testid="billing-success-sub-id">
                                {subscription.provider_subscription_id || "–"}
                            </span>
                            <span className="text-[#0D1B3E]/60">Next renewal</span>
                            <span className="text-right">
                                {new Date(subscription.current_period_end).toLocaleDateString(undefined, {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                })}
                            </span>
                        </div>
                    </>
                )}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={onContinue}
                    className="btn-primary"
                    data-testid="billing-success-continue"
                >
                    Go to workspace
                </button>
                {session.plan_key === "team" && (
                    <Link
                        to="/app/team"
                        className="btn-secondary"
                        data-testid="billing-success-team"
                    >
                        Set up your team
                    </Link>
                )}
                <Link
                    to="/app/billing/portal"
                    className="btn-secondary"
                    data-testid="billing-success-manage"
                >
                    Manage subscription
                </Link>
            </div>
        </div>
    );
}

function FailureShell({ message, onRetry, retryLabel }) {
    return (
        <div className="container-edit py-16 max-w-xl" data-testid="billing-checkout-failed">
            <div className="flex items-center gap-3 text-[#8B1E1E]">
                <XCircle size={22} />
                <span className="text-xs uppercase tracking-[0.22em] font-semibold">
                    Checkout not completed
                </span>
            </div>
            <h1 className="font-display text-2xl lg:text-3xl mt-3 tracking-tight">
                {message}
            </h1>
            <button
                type="button"
                onClick={onRetry}
                className="btn-primary mt-8"
                data-testid="billing-failure-retry"
            >
                {retryLabel}
            </button>
        </div>
    );
}

function OrderSummary({ session }) {
    return (
        <div className="grid grid-cols-2 gap-y-3 text-sm">
            <span className="text-[#0D1B3E]/60">Plan</span>
            <span className="text-right capitalize font-medium" data-testid="billing-summary-plan">
                {session.plan_key}
            </span>
            <span className="text-[#0D1B3E]/60">Billing cycle</span>
            <span className="text-right capitalize">{session.cycle}</span>
            {session.plan_key === "team" && session.seats > 0 && (
                <>
                    <span className="text-[#0D1B3E]/60">Seats</span>
                    <span className="text-right" data-testid="billing-summary-seats">{session.seats}</span>
                </>
            )}
            <span className="text-[#0D1B3E]/60">Amount</span>
            <span className="text-right font-display text-lg">
                €{session.amount_eur.toLocaleString()}
            </span>
            <span className="text-[#0D1B3E]/60">Order ID</span>
            <span className="text-right font-mono text-xs" data-testid="billing-summary-id">
                {session.id.slice(0, 12)}
            </span>
        </div>
    );
}
